/**
 * Error tracking. Mirrors `cubas.core.observability` in the back.
 *
 * With no DSN this is a no-op, which is what `npm run dev` wants: a laptop must not ship
 * its crashes to the production project.
 *
 * Privacy is not optional. These screens show real customer email: names, addresses and
 * prices. `sendDefaultPii` stays off, Session Replay stays off, and `beforeSend` strips
 * what could carry content, so a stack trace never turns into a data leak.
 */
import * as Sentry from '@sentry/react';

import { apiUrl } from '@/shared/config';

const dsn = (import.meta.env.VITE_SENTRY_DSN ?? '').trim();
const environment = (import.meta.env.VITE_APP_ENV ?? 'dev').trim();

/** Query parameters that must never travel inside an event. */
const SENSITIVE_QUERY_PARAMS = ['token', 'password', 'secret', 'key', 'email', 'q'];

/** Redact the sensitive query parameters of a URL, keeping origin and path. */
export function stripSensitiveQuery(value: string): string {
  try {
    const url = new URL(value);
    let touched = false;
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.set(param, '[redacted]');
        touched = true;
      }
    }
    return touched ? url.toString() : value;
  } catch {
    // Not an absolute URL, so there is no query string to clean.
    return value;
  }
}

/**
 * Whether an event is a cancelled request rather than a failure.
 *
 * `AbortError` is the browser saying a fetch was cancelled on purpose: the tab navigated
 * away, an extension pulled the plug, or a script injected from outside (a screenshot
 * harness posting to `127.0.0.1`) got its signal aborted. Nothing in this app aborts its
 * own requests, so none of these point at a bug and reporting them only buries the ones
 * that do. Sentry issue 147954483 was exactly that: a harness, not the app.
 */
export function isAbortedRequest(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some((value) => value.type === 'AbortError');
}

/** Schemes of what a browser extension injects: its violations are not this app's. */
const EXTENSION_SCHEMES = /^(chrome|moz|safari-web|ms-browser)-extension:/i;

/** What a CSP violation is reported as. The origin only: a full URL can carry a query. */
export interface CspViolation {
  directive: string;
  blocked: string;
}

/**
 * The part of a `securitypolicyviolation` event worth a Sentry message, or null when it is
 * noise. The CSP lives in scripts/security-headers.mjs; a violation of it is either an
 * attack that was stopped or, far more likely, something the app needs that the policy
 * forgot, and the second one breaks a screen without an error anywhere else.
 */
export function cspViolation(event: {
  effectiveDirective: string;
  blockedURI: string;
  sourceFile: string;
}): CspViolation | null {
  if (EXTENSION_SCHEMES.test(event.blockedURI) || EXTENSION_SCHEMES.test(event.sourceFile)) {
    return null;
  }
  let blocked = event.blockedURI || 'unknown';
  try {
    const url = new URL(blocked);
    if (url.origin !== 'null') blocked = url.origin;
  } catch {
    // `inline`, `eval`, `data`: keywords, not URLs. They are reported as they come.
  }
  return { directive: event.effectiveDirective, blocked };
}

function reportCspViolations(): void {
  const seen = new Set<string>();
  document.addEventListener('securitypolicyviolation', (event) => {
    const violation = cspViolation(event);
    if (!violation) return;
    const key = `${violation.directive} ${violation.blocked}`;
    // Once per page load: a blocked image in a loop must not spend the quota.
    if (seen.has(key)) return;
    seen.add(key);
    Sentry.captureMessage(`CSP bloqueó ${key}`, {
      level: 'warning',
      tags: { csp_directive: violation.directive },
      extra: { blocked: violation.blocked },
    });
  });
}

export function initObservability(): boolean {
  if (!dsn) {
    return false;
  }

  Sentry.init({
    dsn,
    environment,
    // Never true. See the header comment.
    sendDefaultPii: false,
    // Tracing spends quota and this project's problem is not latency.
    tracesSampleRate: 0,
    // Session Replay would record the screen with the emails on it. Off.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      if (isAbortedRequest(event)) {
        return null;
      }
      if (event.request?.url) {
        event.request.url = stripSensitiveQuery(event.request.url);
      }
      delete event.request?.cookies;
      delete event.request?.headers;
      delete event.user;
      return event;
    },
  });

  Sentry.setTag('process', 'front');
  Sentry.setTag('api_url', apiUrl);
  reportCspViolations();
  return true;
}
